import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, Loader2, CheckCircle2, ArrowRight, ChevronDown, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { FAQ } from "@/lib/faq";
import { BRAND } from "@/lib/brand";
import { VinconexLogo } from "@/components/VinconexMark";
import { LegalLinks } from "@/components/LegalLinks";
import { AssistantChat } from "@/components/AssistantChat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const inputCls = "bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 placeholder:text-slate-600";

function FaqItem({ item, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-[#1C2233]">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`faq-item-${idx}`} aria-expanded={open} className="w-full flex items-center justify-between gap-4 py-4 text-left">
        <span className="font-semibold text-sm sm:text-base">{item.q}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-4 text-sm text-slate-400 leading-relaxed">{item.a}</p>}
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", dealership: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070C] text-white grid-noise">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-[#1C2233]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <VinconexLogo variant="wordmark" className="h-6 w-auto" />
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to="/" className="text-sm text-slate-300 hover:text-white px-3 py-2" data-testid="contact-home-link">Home</Link>
            <Link to="/login">
              <Button variant="outline" data-testid="dealer-login-btn" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">Dealer login</Button>
            </Link>
            <Link to="/signup" className="hidden sm:block">
              <Button data-testid="add-dealership-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] text-white font-bold">Add your dealership</Button>
            </Link>
          </nav>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#0A8CF2]/30 bg-[#0A8CF2]/10 text-[#7FC3FF] text-xs font-mono-tech uppercase tracking-widest mb-5">
            <Mail className="h-3.5 w-3.5" /> Contact us
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">Talk to Vinconex.</h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="contact-direct">
            <a href={BRAND.phoneHref} data-testid="contact-phone-link" className="inline-flex items-center gap-2 text-base sm:text-lg font-bold text-[#7FC3FF] hover:text-white transition-colors">
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a href="mailto:contact@vinconex.com" data-testid="contact-email-link" className="inline-flex items-center gap-2 text-base sm:text-lg font-bold text-[#7FC3FF] hover:text-white transition-colors">
              <Mail className="h-4 w-4" /> contact@vinconex.com
            </a>
          </div>
          <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl">
            Questions about packages, the Specialist Match add-on, CRM delivery or going live at your rooftop? Ask the assistant for an instant answer, or leave a message — our AI drafts a first reply right away and a team member follows up within one business day.
          </p>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 grid lg:grid-cols-2 gap-8">
        <AssistantChat onHandoff={() => setHighlight(true)} />

        <div id="contact-form" className={`tracing-top rounded-2xl border bg-[#0B0F18] p-7 transition-colors ${highlight ? "border-[#0A8CF2]/70" : "border-[#1C2233]"}`}>
          {done ? (
            <div data-testid="contact-success" className="text-center py-10">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
              <h2 className="font-heading text-2xl font-bold mt-4">Message received, {form.name.split(" ")[0]}!</h2>
              <p className="text-slate-400 mt-3 text-sm max-w-sm mx-auto">
                Check your inbox — our assistant has sent a first reply to <span className="text-slate-200">{form.email}</span>. A Vinconex team member will follow up within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-[#0A8CF2]" /> Leave a message</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 mb-1.5 block">Your name</Label>
                  <Input required data-testid="contact-name-input" value={form.name} onChange={set("name")} className={inputCls} />
                </div>
                <div>
                  <Label className="text-slate-300 mb-1.5 block">Dealership</Label>
                  <Input data-testid="contact-dealership-input" value={form.dealership} onChange={set("dealership")} placeholder="Abeloff Nissan" className={inputCls} />
                </div>
                <div>
                  <Label className="text-slate-300 mb-1.5 block">Email</Label>
                  <Input required type="email" data-testid="contact-email-input" value={form.email} onChange={set("email")} placeholder="you@dealership.com" className={inputCls} />
                </div>
                <div>
                  <Label className="text-slate-300 mb-1.5 block">Phone (optional)</Label>
                  <Input type="tel" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 555-0100" className={inputCls} />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 mb-1.5 block">How can we help?</Label>
                <Textarea required data-testid="contact-message-input" value={form.message} onChange={set("message")} placeholder="Tell us about your rooftop(s), your CRM and what you'd like to do with Vinconex…" className={`${inputCls} min-h-[130px]`} />
              </div>
              <Button type="submit" disabled={busy} data-testid="contact-submit-btn" className="w-full h-12 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold text-base">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send message <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">Goes to contact@vinconex.com · you'll get an instant first reply from our assistant.</p>
            </form>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24" data-testid="faq-section">
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">Frequently asked</h2>
        <p className="text-slate-400 text-sm sm:text-base mb-6">The questions dealers ask before they put the first sticker on a windshield.</p>
        <div className="grid lg:grid-cols-2 gap-x-10">
          {FAQ.map((item, i) => <FaqItem key={item.q} item={item} idx={i} />)}
        </div>
      </section>

      <footer className="border-t border-[#1C2233] py-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <VinconexLogo variant="wordmark" className="h-4 w-auto opacity-70" />
          <LegalLinks />
          <span data-testid="owner-line"><a href={BRAND.phoneHref} className="font-semibold hover:text-white">{BRAND.phone}</a> · contact@vinconex.com · © {new Date().getFullYear()} {BRAND.owner} · Vinconex is a {BRAND.owner} platform</span>
        </div>
      </footer>
    </div>
  );
}
